/* eslint-disable react-refresh/only-export-components */
import { Suspense, useState, type ComponentType } from 'react';
import {
  createBrowserRouter,
  Link,
  Navigate,
  RouterProvider,
  matchPath,
  useParams,
  useRouteError,
} from 'react-router-dom';
import { AppLayout } from '../components/Layout';
import { getCalculator, type CalculatorDefinition } from '../data/site';
import { site } from '../lib/site';
import { useLocale } from '../context/LocaleContext';
import { RouteLoadingState } from '../components/LoadingStates';
import { timedImport } from '../lib/performance';
import type { Directory } from '../features/solar-company-search/pages/SolarCompanySearchPage';
import {
  appRoutePatterns,
  blockedRoutePaths,
  redirectRoutePatterns,
  type AppRouteId,
} from './route-registry';

type PreloadableComponent<Props extends object> = ComponentType<Props> & {
  preload: () => Promise<void>;
};

function preloadable<Props extends object>(
  loader: () => Promise<{ default: ComponentType<Props> }>,
): PreloadableComponent<Props> {
  let loaded: ComponentType<Props> | undefined;
  let pending: Promise<void> | undefined;
  const preload = () => {
    pending ??= loader().then((module) => {
      loaded = module.default;
    });
    return pending;
  };
  const Component = (props: Props) => {
    if (!loaded) throw preload();
    const Loaded = loaded;
    return <Loaded {...props} />;
  };
  Component.preload = preload;
  return Component;
}

const HomePage = preloadable(() => timedImport('home', () => import('../pages/HomePage')));
const CalculatorPage = preloadable<{ definition: CalculatorDefinition }>(() =>
  timedImport('calculator-page', () =>
    import('../components/CalculatorPage').then((module) => ({ default: module.CalculatorPage })),
  ),
);
const CalculatorDirectoryPage = preloadable(() =>
  timedImport('calculator-directory', () => import('../pages/DirectoryPages')),
);
const StaticPage = preloadable(() =>
  timedImport('static-page', () => import('../pages/StaticPages')),
);
const HubPage = preloadable(() => timedImport('hub-page', () => import('../pages/HubPages')));
const GuidePage = preloadable(() => timedImport('guide-page', () => import('../pages/GuidePage')));
const GuidesPage = preloadable(() =>
  timedImport('guides-index', () => import('../pages/GuidesPage')),
);
const CaseStudyPages = preloadable(() =>
  timedImport('case-studies', () => import('../features/case-studies/CaseStudyPages')),
);
const SolarCompare = preloadable<Record<string, never>>(() =>
  timedImport('solar-compare', () =>
    import('../pages/SolarComparePages').then((m) => ({ default: m.SolarComparePage })),
  ),
);
const QuoteCompare = preloadable<Record<string, never>>(() =>
  timedImport('quote-compare', () =>
    import('../pages/SolarComparePages').then((m) => ({ default: m.QuoteComparePage })),
  ),
);
const SolarCompanySearchPage = preloadable<Record<string, never>>(() =>
  timedImport('installer-directory', () =>
    Promise.all([
      import('../features/solar-company-search/pages/SolarCompanySearchPage'),
      import('../../public/data/solar-installers-global.json'),
    ]).then(([module, directory]) => ({
      default: function HydrationStableSolarCompanySearchPage() {
        return <module.default initialDirectory={directory.default as Directory} />;
      },
    })),
  ),
);
const SolarAssessmentPage = preloadable(() =>
  timedImport('solar-assessment', () => import('../pages/SolarAssessmentPage')),
);
const InstallerChecklistPage = preloadable(() =>
  timedImport('installer-checklist', () => import('../pages/InstallerChecklistPage')),
);
const SolarPlanWorkspacePage = preloadable(() =>
  timedImport(
    'solar-plan-workspace',
    () => import('../features/solar-plan/SolarPlanWorkspacePage'),
  ),
);
const TopicClusterPage = preloadable(() =>
  timedImport('topic-cluster', () => import('../pages/TopicClusterPage')),
);
const SolarFaqPage = preloadable(() =>
  timedImport('solar-faq', () => import('../pages/SolarFaqPage')),
);
const SeoLandingPage = preloadable(() =>
  timedImport('seo-landing', () => import('../pages/SeoLandingPage')),
);
const SolarDecisionServicesPage = preloadable(() =>
  timedImport('solar-decision-services', () => import('../pages/SolarDecisionServicesPage')),
);

const routePreloaders: Partial<Record<AppRouteId, () => Promise<void>>> = {
  home: HomePage.preload,
  solarAssessment: SolarAssessmentPage.preload,
  installerChecklist: InstallerChecklistPage.preload,
  mySolarPlan: SolarPlanWorkspacePage.preload,
  calculatorDirectory: CalculatorDirectoryPage.preload,
  calculatorCategory: CalculatorDirectoryPage.preload,
  electricityHub: HubPage.preload,
  electricityCalculator: CalculatorPage.preload,
  solarHub: HubPage.preload,
  solarCalculator: CalculatorPage.preload,
  solarCompare: SolarCompare.preload,
  quoteCompare: QuoteCompare.preload,
  solarCompanies: SolarCompanySearchPage.preload,
  installerCompany: SolarCompanySearchPage.preload,
  installerProvince: SolarCompanySearchPage.preload,
  installerCity: SolarCompanySearchPage.preload,
  guides: GuidesPage.preload,
  guide: GuidePage.preload,
  caseStudies: CaseStudyPages.preload,
  caseStudy: CaseStudyPages.preload,
  about: StaticPage.preload,
  methodology: StaticPage.preload,
  privacy: StaticPage.preload,
  terms: StaticPage.preload,
  disclaimer: StaticPage.preload,
  contact: StaticPage.preload,
  advertise: StaticPage.preload,
  corrections: StaticPage.preload,
  sources: StaticPage.preload,
  topicCluster: TopicClusterPage.preload,
  solarFaq: SolarFaqPage.preload,
  solarService: SolarDecisionServicesPage.preload,
  seoLanding: SeoLandingPage.preload,
};

export async function preloadCurrentRoute(pathname: string) {
  const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  const route = appRoutePatterns.find((candidate) =>
    candidate.index
      ? normalizedPath === '/'
      : Boolean(
          candidate.path && matchPath({ path: `/${candidate.path}`, end: true }, normalizedPath),
        ),
  );
  const preload = route ? routePreloaders[route.id] : undefined;
  if (preload) await preload();
}

function Loading() {
  return <RouteLoadingState />;
}

function CalculatorRoute() {
  const { calculatorId } = useParams();
  const definition = getCalculator(calculatorId ?? '');
  return definition ? suspense(<CalculatorPage definition={definition} />) : <NotFound />;
}

function RouteErrorPage() {
  const error = useRouteError();
  if (import.meta.env.DEV) console.error('Route error', error);
  return <SafeRouteErrorView />;
}

export function SafeRouteErrorView() {
  const { t } = useLocale();
  const [reference] = useState(() =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8).toUpperCase()
      : Math.random().toString(36).slice(2, 10).toUpperCase(),
  );

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-16">
      <section className="mx-auto max-w-2xl rounded-xl border border-amber-200 bg-white p-7 shadow-sm sm:p-10">
        <p className="text-sm font-black uppercase tracking-[.16em] text-amber-700">
          SolarGabay PH
        </p>
        <h1 className="mt-4 text-3xl font-black text-navy-950">{t('common.routeErrorTitle')}</h1>
        <p className="mt-4 leading-7 text-slate-600">{t('common.routeErrorBody')}</p>
        <p className="mt-4 rounded-xl bg-slate-100 p-4 text-sm font-bold text-slate-700">
          {t('common.supportReference')}: {reference}
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <button className="btn-primary" type="button" onClick={() => window.location.reload()}>
            {t('common.reload')}
          </button>
          <Link className="btn-secondary" to="/">
            {t('common.returnHome')}
          </Link>
          <a
            className="btn-secondary"
            href={`mailto:${site.contactEmail}?subject=Site%20error%20${reference}`}
          >
            {t('common.contactSupport')}
          </a>
        </div>
      </section>
    </main>
  );
}

function NotFound() {
  const { t } = useLocale();
  return (
    <section className="container-page py-20">
      <h1 className="text-4xl font-black">{t('common.pageNotFound')}</h1>
      <p className="mt-4 text-slate-600">{t('common.pageNotFoundBody')}</p>
    </section>
  );
}

const suspense = (node: React.ReactNode) => <Suspense fallback={<Loading />}>{node}</Suspense>;

function browserElement(id: AppRouteId) {
  switch (id) {
    case 'home':
      return suspense(<HomePage />);
    case 'solarAssessment':
      return suspense(<SolarAssessmentPage />);
    case 'installerChecklist':
      return suspense(<InstallerChecklistPage />);
    case 'mySolarPlan':
      return suspense(<SolarPlanWorkspacePage />);
    case 'calculatorDirectory':
    case 'calculatorCategory':
      return suspense(<CalculatorDirectoryPage />);
    case 'electricityHub':
      return suspense(<HubPage type="electricity" />);
    case 'electricityCalculator':
    case 'solarCalculator':
      return <CalculatorRoute />;
    case 'solarHub':
      return suspense(<HubPage type="solar" />);
    case 'solarCompare':
      return suspense(<SolarCompare />);
    case 'quoteCompare':
      return suspense(<QuoteCompare />);
    case 'solarCompanies':
    case 'installerCompany':
    case 'installerProvince':
    case 'installerCity':
      return suspense(<SolarCompanySearchPage />);
    case 'guides':
      return suspense(<GuidesPage />);
    case 'guide':
      return suspense(<GuidePage />);
    case 'caseStudies':
    case 'caseStudy':
      return suspense(<CaseStudyPages />);
    case 'about':
    case 'methodology':
    case 'privacy':
    case 'terms':
    case 'disclaimer':
    case 'contact':
    case 'advertise':
    case 'corrections':
    case 'sources':
      return suspense(<StaticPage page={id} />);
    case 'topicCluster':
      return suspense(<TopicClusterPage />);
    case 'solarFaq':
      return suspense(<SolarFaqPage />);
    case 'solarService':
      return suspense(<SolarDecisionServicesPage />);
    case 'seoLanding':
      return suspense(<SeoLandingPage />);
  }
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      ...blockedRoutePaths.map((path) => ({ path, element: <NotFound /> })),
      ...appRoutePatterns.map((route) =>
        route.index
          ? { index: true as const, element: browserElement(route.id) }
          : { path: route.path, element: browserElement(route.id) },
      ),
      ...redirectRoutePatterns.map((route) => ({
        path: route.path,
        element: <Navigate replace to={route.to} />,
      })),
      { path: '*', element: <NotFound /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
